import { api, ApiError } from '@/lib/api';
import type { MedicalRecord } from '@/features/medical-record/types';
import type {
  Booking,
  BookingDetail,
  CreateBookingRequest,
  CreateBookingResponse,
  ResumePaymentResponse,
  SlotsResponse,
  VerifyPaymentRequest,
} from './types';

export async function fetch_slots(service_id: string, date: string): Promise<SlotsResponse> {
  const res = await api.get<SlotsResponse>(`/user/services/${service_id}/slots`, { params: { date } });
  return res.data;
}

export async function create_booking(input: CreateBookingRequest): Promise<CreateBookingResponse> {
  const res = await api.post<CreateBookingResponse>('/user/bookings', input);
  return res.data;
}

/** The finalized clinical record for a visit, or null if none is published yet. */
export async function fetch_booking_record(id: string): Promise<MedicalRecord | null> {
  try {
    const res = await api.get<MedicalRecord>(`/user/bookings/${id}/record`);
    return res.data;
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

export async function verify_payment(id: string, input: VerifyPaymentRequest): Promise<Booking> {
  const res = await api.post<Booking>(`/user/bookings/${id}/pay/verify`, input);
  return res.data;
}

/**
 * Re-open the checkout for a booking whose payment was started and never finished.
 *
 * The `checkout` object only ever came back from creating the booking, so a customer who
 * killed the app mid-payment had no way back to it. Reuses the SAME gateway order rather
 * than minting a new one.
 */
export async function resume_payment(id: string): Promise<ResumePaymentResponse> {
  const res = await api.post<ResumePaymentResponse>(`/user/bookings/${id}/pay/resume`);
  return res.data;
}

export async function pay_booking(id: string): Promise<Booking> {
  const res = await api.post<Booking>(`/user/bookings/${id}/pay`);
  return res.data;
}

export async function fetch_my_bookings(): Promise<Booking[]> {
  const res = await api.get<Booking[]>('/user/bookings');
  return res.data;
}

/** Enriched single booking (service, vendor, pet, plan context, walk report, incidents). */
export async function fetch_booking_detail(id: string): Promise<BookingDetail> {
  const res = await api.get<BookingDetail>(`/user/bookings/${id}`);
  return res.data;
}

/** Same shape from the vendor's side — counterparty is the customer, not the vendor. */
export async function fetch_vendor_booking_detail(id: string): Promise<BookingDetail> {
  const res = await api.get<BookingDetail>(`/vendor/bookings/${id}`);
  return res.data;
}

export async function cancel_booking(id: string, reason?: string): Promise<Booking> {
  const res = await api.post<Booking>(`/user/bookings/${id}/cancel`, reason ? { reason } : undefined);
  return res.data;
}

export async function reschedule_booking(id: string, slot_start: string): Promise<Booking> {
  const res = await api.post<Booking>(`/user/bookings/${id}/reschedule`, { slot_start });
  return res.data;
}
